import { ConfigProvider, theme as antdTheme, App as AntApp } from 'antd';
import { useEffect, type ReactNode } from 'react';
import { useSettingsStore, COLOR_THEMES } from '@store/settingsStore';

export function ThemeProvider({ children }: { children: ReactNode }) {
  const theme = useSettingsStore((s) => s.theme);
  const colorThemeKey = useSettingsStore((s) => s.colorTheme);
  const compactMode = useSettingsStore((s) => s.compactMode);

  const selectedTheme = COLOR_THEMES.find((t) => t.key === colorThemeKey) || COLOR_THEMES[0];
  const primaryColor = selectedTheme.color;

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.setProperty('--color-primary', primaryColor);
    document.documentElement.style.setProperty('--color-primary-rgb', hexToRgb(primaryColor));
    document.body.style.background = theme === 'dark' ? '#0f0f12' : '#f8fafc';
  }, [theme, primaryColor]);

  const algorithms = [
    theme === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    ...(compactMode ? [antdTheme.compactAlgorithm] : []),
  ];

  return (
    <ConfigProvider
      theme={{
        algorithm: algorithms,
        token: {
          colorPrimary: primaryColor,
          borderRadius: 8,
          borderRadiusLG: 12,
          borderRadiusSM: 6,
          fontFamily:
            'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, sans-serif',
          colorBgBase: theme === 'dark' ? '#0f0f12' : '#ffffff',
          colorBgContainer: theme === 'dark' ? '#15151a' : '#ffffff',
          colorBorderSecondary: theme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
        },
        components: {
          Layout: {
            headerBg: theme === 'dark' ? '#15151a' : '#ffffff',
            siderBg: theme === 'dark' ? '#15151a' : '#ffffff',
            bodyBg: theme === 'dark' ? '#0f0f12' : '#f8fafc',
          },
          Card: {
            headerBg: 'transparent',
            borderRadiusLG: 12,
            boxShadowTertiary: theme === 'dark'
              ? '0 4px 20px -2px rgba(0,0,0,0.3)'
              : '0 4px 20px -2px rgba(15, 23, 42, 0.04)',
          },
          Button: {
            borderRadius: 6,
            controlHeight: 36,
          },
          Input: {
            borderRadius: 6,
            controlHeight: 36,
          },
          Select: {
            borderRadius: 6,
            controlHeight: 36,
          },
          Menu: {
            itemBg: 'transparent',
            itemSelectedBg: theme === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.015)',
          },
        },
      }}
    >
      <AntApp>{children}</AntApp>
    </ConfigProvider>
  );
}

function hexToRgb(hex: string): string {
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  const fullHex = hex.replace(shorthandRegex, (_, r, g, b) => r + r + g + g + b + b);
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(fullHex);
  return result
    ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
    : '37, 99, 235';
}
